import { Injectable } from '@angular/core';
import { IToken } from '../interfaces/i-token';
import { DataStorageService } from './data-storage.service';
import { StorageService } from '@clavisco/core';

/**
 * AuthenticationService provides methods for managing user authentication.
 * It includes functionality to check authentication status, convert date formats, and log out users.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  /**
   * Constructs the AuthenticationService.
   *
   * @param {DataStorageService} dataStorageService - Service for managing data storage.
   * @param {StorageService} storageService - Service for managing storage operations.
   */
  constructor(
    private dataStorageService: DataStorageService,
    private storageService: StorageService
  ) {}

  /**
   * Checks if the user is authenticated based on the current session token.
   *
   * @returns {boolean} True if the user is authenticated, false otherwise.
   */
  public IsAuthenticated(): boolean {
    try {
      const currentSession =
        this.dataStorageService.GetItem<IToken>('currentUser');
      if (currentSession === null) return false;

      const TOKEN = currentSession;

      if (TOKEN) {
        const expires: Date = new Date(TOKEN.expires);
        const today: Date = new Date();
        const currentUTCDate: Date = new Date(
          today.getTime() + today.getTimezoneOffset() * 60000
        ); // Convert to UTC

        if (currentUTCDate < expires) {
          // Token exists and has not expired, it is valid
          return true;
        }
      }
    } catch (error) {
      console.error('Error decoding session:', error);
      return false;
    }
    return false;
  }

  /**
   * Converts a date string from 'DD/MM/YYYY HH:mm:ss' format to ISO 8601 format.
   *
   * @param {string} dateString - The date string to convert.
   * @returns {string} The converted date string in ISO 8601 format.
   */
  convertDate(dateString: string): string {
    const [datePart, timePart] = dateString.split(' ');
    const [day, month, year] = datePart.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(
      2,
      '0'
    )}T${timePart}`;
  }

  /**
   * Logs out the user by clearing storage and removing the current session token.
   */
  public Logout(): void {
    this.storageService.CleanStorage();
    this.dataStorageService.RemoveItem<IToken>('currentUser');
  }
}
